import { APIError } from "@certn/api";
import {
  ActionList,
  BasicButton,
  FieldErrors,
  RequirementsList,
  Stack,
  Text,
} from "@certn/cascadia";
import { CascadiaJsonFormContext } from "@certn/forms/src/CascadiaJsonFormContext";
import { getRequirementsList } from "@certn/forms/src/controls/utils";
import { CascadiaFormSubmissionError } from "@certn/forms/src/errors";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "@certn/forms/src/wrappers/withCascadiaControlProps";
import { filterNestedPaths, get, slugifyPath } from "@certn/utils";
import { findUISchema, Resolve } from "@jsonforms/core";
import { JsonFormsContext } from "@jsonforms/react";
import { cloneDeep, set } from "lodash";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ArrayModalControlContext } from "./ArrayModalControlContext";
import { ArrayModalControlDialog } from "./ArrayModalControlDialog";
import { ArrayModalControlListItem } from "./ArrayModalControlListItem";
import { ArrayModalControlSummary } from "./ArrayModalControlSummary";
import { ApplicationContext } from "../../ApplicationContext";
import { parseSubmitErrors } from "../../utils";

/**
 * Props interface for the ArrayModalControl component.
 * Extends CascadiaControlProps and includes optional render functions and an onChange handler.
 */
interface Props<T extends Record<string, unknown>>
  extends CascadiaControlProps {
  /**
   * Function to render a custom tag for an item.
   * @param item - The item to render the tag for.
   * @returns A React node representing the tag.
   */
  renderTag?: (item: T) => React.ReactNode;

  /**
   * Function to render a custom title for an item.
   * @param item - The item to render the title for.
   * @returns A React node representing the title.
   */
  renderTitle?: (item: T) => React.ReactNode;

  /**
   * Function to render a custom subtitle for an item.
   * @param item - The item to render the subtitle for.
   * @returns A React node representing the subtitle.
   */
  renderSubtitle?: (item: T) => React.ReactNode;

  /**
   * Callback function triggered when the value changes.
   * @param value - The new value.
   */
  onChange?: (value: T[]) => void;

  /**
   * Optional comparator to sort items for display only (original indices preserved for form paths).
   */
  sortComparator?: (a: T, b: T) => number;
}

/**
 * ArrayModalControl component.
 * A form control for managing an ActionList of items with modal dialogs.
 *
 * @param props - The properties for the component.
 * @returns A React element representing the control.
 */
export function ArrayModalControl(props: Props<Record<string, unknown>>) {
  const { options, onChange } = props;
  const { t } = useTranslation();
  const label = options.itemName?.toLowerCase() || props.label.toLowerCase();
  // `label` is the translated item name ("Address"). The schema path
  // (`residences`) does not move when that copy or locale changes.
  const testIdPrefix = slugifyPath(props.path);

  const context = useContext(JsonFormsContext);
  const form = useContext(CascadiaJsonFormContext);
  const application = useContext(ApplicationContext);

  const data: Record<string, unknown>[] = useMemo(
    () => (Array.isArray(props.data) ? props.data : []),
    [props.data]
  );

  const childSchema = Resolve.schema(props.schema, "items", props.rootSchema);

  const childUiSchema = useMemo(
    () =>
      findUISchema(
        context.uischemas || [],
        props.schema,
        props.uischema.scope,
        props.path,
        undefined,
        props.uischema,
        props.rootSchema
      ),
    [
      context.uischemas,
      props.schema,
      props.uischema,
      props.path,
      props.rootSchema,
    ]
  );

  /**
   * Handles submission of an item.
   *
   * @param itemPath - The path of the item being submitted.
   * @param values - The values to submit.
   * @throws CascadiaFormSubmissionError if there are validation errors.
   */
  const handleSubmit = useCallback(
    async (itemPath: string, values: unknown) => {
      props.setTouched();

      const updatedFormValues = set(cloneDeep(form.values), itemPath, values);

      try {
        const savedData = await application.saveData(updatedFormValues, true);
        form.setValues(savedData);
      } catch (e) {
        if (e instanceof APIError && e.status === 400) {
          const sectionErrors = parseSubmitErrors(e) || {};
          const itemErrors = filterNestedPaths(sectionErrors, itemPath);

          if (Object.keys(itemErrors).length > 0) {
            throw new CascadiaFormSubmissionError(itemErrors);
          }

          const savedData = await application.saveData(
            updatedFormValues,
            false
          );
          form.setValues(savedData);

          return;
        }

        throw e;
      }
    },
    [props, form, application]
  );

  /**
   * Handles deletion of an item.
   *
   * @param itemPath - The path of the item being deleted.
   */
  const handleDelete = useCallback(
    async (itemPath: string) => {
      props.setTouched();

      const pathParts = itemPath.split(".");
      const parentPath = pathParts.slice(0, -1).join(".");
      const index = parseInt(pathParts[pathParts.length - 1], 10);

      if (Number.isNaN(index)) {
        return;
      }

      const value = get(parentPath, form.values);
      if (!Array.isArray(value)) {
        return;
      }

      const updatedFormValues = set(
        cloneDeep(form.values),
        parentPath,
        value.filter((_, i) => i !== index)
      );

      const savedData = await application.saveData(updatedFormValues, false);
      form.setValues(savedData);
    },
    [props, form, application]
  );

  useEffect(() => {
    onChange?.(data);
  }, [data, onChange]);

  const residencesProperty = props.rootSchema.properties?.[props.path] as {
    requirements?: string[];
    nzCurrentRegionMinMonths?: number;
    errorMessage?: Record<string, string>;
  };

  const hasNzCurrentAddress =
    Array.isArray(data) &&
    data.some(
      (item: { end_date?: string | null; address?: { country?: string } }) =>
        (item.end_date === undefined ||
          item.end_date === null ||
          item.end_date === "") &&
        item?.address?.country?.toUpperCase() === "NZ"
    );

  const baseRequirements = residencesProperty?.requirements?.filter(
    (msg: string) =>
      !msg.toLowerCase().includes("postcode") ||
      (Array.isArray(data) &&
        data.some(
          (item: { address?: { country?: string } }) =>
            item?.address?.country === "GB"
        ))
  );

  const requirementMessages = useMemo(() => {
    const nzCurrentRegionMinMonths =
      residencesProperty?.nzCurrentRegionMinMonths;
    const minMonthsMessage = residencesProperty?.errorMessage?.minMonths;
    const nzMessage =
      residencesProperty?.errorMessage?.nzCurrentRegionMinMonths;

    if (nzCurrentRegionMinMonths && hasNzCurrentAddress && nzMessage) {
      return [
        ...(baseRequirements ?? []).filter((msg) => msg !== minMonthsMessage),
        nzMessage,
      ];
    }

    return baseRequirements;
  }, [
    baseRequirements,
    hasNzCurrentAddress,
    residencesProperty?.errorMessage?.minMonths,
    residencesProperty?.errorMessage?.nzCurrentRegionMinMonths,
    residencesProperty?.nzCurrentRegionMinMonths,
  ]);

  const canAddMoreItems = useMemo(() => {
    if (!options.maxItems || !Array.isArray(props.data)) return true;
    return props.data.length < options.maxItems;
  }, [options.maxItems, props.data]);

  const sortedItems = useMemo(() => {
    const items = data.map((item, index) => ({ item, originalIndex: index }));
    if (props.sortComparator) {
      items.sort((a, b) => props.sortComparator!(a.item, b.item));
    }
    return items;
  }, [data, props.sortComparator]);

  const contextValue = useMemo(
    () => ({
      label,
      testIdPrefix,
      childSchema,
      childUiSchema,
      options,
      data,
      renderTag: props.renderTag,
      renderSubtitle: props.renderSubtitle,
      handleSubmit,
      handleDelete,
    }),
    [
      label,
      testIdPrefix,
      childSchema,
      childUiSchema,
      options,
      data,
      props.renderTag,
      props.renderSubtitle,
      handleSubmit,
      handleDelete,
    ]
  );

  if (form.isSummary) {
    return <ArrayModalControlSummary {...props} />;
  }

  return (
    <ArrayModalControlContext.Provider value={contextValue}>
      <Stack gap="space-1" testID={props.testID}>
        {options.showLabel && props.label && (
          <Text style="input/label">{props.label}</Text>
        )}
        <Stack gap="space-3">
          {data.length > 0 && (
            <ActionList hideBorder={false}>
              {sortedItems.map(({ item, originalIndex }) => {
                const itemPath = `${props.path}.${originalIndex}`;
                const errors = filterNestedPaths(form.submitErrors, itemPath);

                return (
                  <ArrayModalControlListItem
                    key={get("id", item, originalIndex)}
                    item={item}
                    itemPath={itemPath}
                    errors={errors}
                    onSubmit={handleSubmit}
                    onDelete={handleDelete}
                  />
                );
              })}
            </ActionList>
          )}
          {canAddMoreItems && (
            <ArrayModalControlDialog
              title={t("Add {{label}}", { label })}
              itemPath={`${props.path}.${data.length}`}
              trigger={({ toggle }) => (
                <BasicButton
                  leftIcon="add"
                  onPress={toggle}
                  testID={`${testIdPrefix}-add-button`}
                >
                  {t("Add {{label}}", { label })}
                </BasicButton>
              )}
              onSubmit={handleSubmit}
            />
          )}
          {requirementMessages && (
            <RequirementsList
              requirements={getRequirementsList(
                requirementMessages,
                props.errors,
                props.touched,
                props.label
              )}
            />
          )}
          {!requirementMessages && <FieldErrors>{props.errors}</FieldErrors>}
        </Stack>
      </Stack>
    </ArrayModalControlContext.Provider>
  );
}

ArrayModalControl.displayName = "ArrayModalControl";

/**
 * Renderer for the ArrayModalControl component.
 * Wraps the component with Cascadia control properties.
 *
 * @see {@link ArrayModalControl} for the detailed control component.
 */
export const ArrayModalControlRenderer = withCascadiaControlProps(
  ArrayModalControl,
  { alwaysShowErrors: true }
);
